import React, { useRef } from 'react';
import { LexicalEditor } from 'lexical';
import { Popover, PopoverBody } from 'reactstrap';
import { useToggle } from '@hooks/useToggle';
import { ColorPicker } from './ColorPicker';
import { ToolbarIconButton } from '../ToolbarIconButton';
import { applyStyleToSelection, TOOLBAR_POPOVER_PROPS, FONT_SIZES } from './utils';

interface OverflowMenuProps {
  editor: LexicalEditor;
  fontSize: string;
  fontColor: string;
}

/**
 * Mobile-responsive overflow menu that combines font size and color controls
 * into a single popover triggered by a three-dot icon.
 */
export const OverflowMenu = ({
  editor,
  fontSize,
  fontColor,
}: OverflowMenuProps): React.ReactNode => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [popoverOpen, toggle] = useToggle(false);

  const handleFontSizeChange = (size: string) => {
    applyStyleToSelection(editor, { 'font-size': size });
  };

  const handleColorChange = (color: string) => {
    applyStyleToSelection(editor, { color });
  };

  return (
    <div className='toolbar-overflow'>
      <ToolbarIconButton
        ref={buttonRef}
        icon='fa-ellipsis-h'
        onClick={toggle}
        isActive={popoverOpen}
      />
      <Popover
        {...TOOLBAR_POPOVER_PROPS}
        isOpen={popoverOpen}
        toggle={toggle}
        target={buttonRef}
      >
        <PopoverBody className='editor__toolbar__submenu p-2'>
          <div className='mb-2'>
            <small className='text-muted'>Font Size</small>
            <div className='d-flex flex-wrap gap-1 mt-1'>
              {FONT_SIZES.map(({ label, value }) => (
                <button
                  key={value}
                  onClick={() => handleFontSizeChange(value)}
                  className={`btn btn-sm ${fontSize === value ? 'btn-primary' : 'btn-outline-secondary'}`}
                  type='button'
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <small className='text-muted'>Font Color</small>
            <div className='mt-1'>
              <ColorPicker color={fontColor} onChange={handleColorChange} />
            </div>
          </div>
        </PopoverBody>
      </Popover>
    </div>
  );
};
